import { DisplayLogo } from "@/components/Common/DisplayLogo"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  useDeleteLogoMutation,
  useUpdateLogoMutation,
} from "@/query/shopDataQuery"
import { Cross2Icon, ImageIcon, UploadIcon } from "@radix-ui/react-icons"
import { TrashIcon } from "lucide-react"
import React, { useRef, useState } from "react"

type EditLogoModalProps = {
  isOpen: boolean
  currentLogoUrl?: string
  shopName: string
  onClose: () => void
}

export const EditLogoModal: React.FC<EditLogoModalProps> = ({
  isOpen,
  currentLogoUrl,
  shopName,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    updateLogoMutation,
    isUploadingLogo,
    uploadLogoError,
    isUploadLogoError,
  } = useUpdateLogoMutation()

  const {
    deleteLogoMutation,
    isDeletingLogo,
    deleteLogoError,
    isDeleteLogoError,
  } = useDeleteLogoMutation()

  const handleFileSelect = (file: File) => {
    // Validation des types de fichiers
    const supportedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ]
    if (!supportedTypes.includes(file.type)) {
      alert("Type de fichier non supporté. Utilisez JPG, PNG, WebP ou GIF.")
      return
    }

    // Validation de la taille (5MB max)
    const maxSize = 5 * 1024 * 1024 // 5MB
    if (file.size > maxSize) {
      alert(
        `Fichier trop volumineux. Taille maximum: 5MB, reçu: ${
          Math.round((file.size / 1024 / 1024) * 100) / 100
        }MB`
      )
      return
    }

    setSelectedFile(file)
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleSave = () => {
    if (!selectedFile) return

    updateLogoMutation(
      { file: selectedFile },
      {
        onSuccess: () => {
          handleClose()
        },
        onError: (error) => {
          console.error("Erreur upload logo:", error)
        },
      }
    )
  }

  const handleDeleteLogo = () => {
    deleteLogoMutation(undefined, {
      onSuccess: () => {
        handleClose()
      },
      onError: (error) => {
        console.error("Erreur suppression logo:", error)
      },
    })
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setIsDragOver(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    onClose()
  }

  const removeSelectedFile = () => {
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <ImageIcon className="h-5 w-5 mr-2 text-blue-500" />
            Modifier le logo de la boutique
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un nouveau logo pour votre boutique "{shopName}".
            Formats acceptés : JPG, PNG, WebP, GIF (max 5MB).
          </DialogDescription>
        </DialogHeader>

        {/* Input de fichier */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />

        <div className="space-y-6">
          {/* Logo actuel */}
          {currentLogoUrl && (
            <div className="space-y-3">
              <Label>Logo actuel</Label>
              <div className="flex justify-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                <DisplayLogo
                  urlLogo={currentLogoUrl}
                  shopName={shopName}
                  size="large"
                />
              </div>
            </div>
          )}

          {/* Affichage des erreurs */}
          {isUploadLogoError && uploadLogoError && (
            <Alert variant="destructive">
              <AlertDescription>
                Erreur upload :{" "}
                {uploadLogoError?.message ||
                  "Une erreur inconnue s'est produite"}
              </AlertDescription>
            </Alert>
          )}

          {isDeleteLogoError && deleteLogoError && (
            <Alert variant="destructive">
              <AlertDescription>
                Erreur suppression :{" "}
                {deleteLogoError?.message ||
                  "Une erreur inconnue s'est produite"}
              </AlertDescription>
            </Alert>
          )}

          {/* Zone de sélection de fichier */}
          <div className="space-y-3">
            <Label>Nouveau logo</Label>

            {!selectedFile ? (
              <div
                className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                  isDragOver
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 cursor-pointer"
                    : "border-slate-300 dark:border-slate-600 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileSelector}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <UploadIcon className="h-8 w-8 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-slate-700 dark:text-slate-200">
                      Glissez-déposez votre logo ici
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      ou cliquez pour parcourir vos fichiers
                    </p>
                  </div>
                  <div className="text-xs text-slate-400 dark:text-slate-500">
                    Taille maximale : 5 MB
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Aperçu du nouveau logo */}
                <div className="space-y-3">
                  <Label>Aperçu du nouveau logo</Label>
                  <div className="relative flex justify-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border">
                    <img
                      src={previewUrl || ""}
                      alt="Aperçu du nouveau logo"
                      className="h-20 w-20 object-contain rounded-full"
                    />
                    <button
                      onClick={removeSelectedFile}
                      className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors duration-200"
                      aria-label="Supprimer le fichier"
                      disabled={isUploadingLogo || isDeletingLogo}
                    >
                      <Cross2Icon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Informations sur le fichier */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openFileSelector}
                      disabled={isUploadingLogo || isDeletingLogo}
                    >
                      Changer
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-start pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isUploadingLogo || isDeletingLogo}
          >
            Annuler
          </Button>

          {/* Bouton supprimer le logo (uniquement si un logo existe) */}
          {currentLogoUrl && (
            <Button
              type="button"
              onClick={handleDeleteLogo}
              disabled={isUploadingLogo || isDeletingLogo}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {isDeletingLogo ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Supprimer le logo
                </>
              )}
            </Button>
          )}

          <Button
            type="button"
            onClick={handleSave}
            disabled={!selectedFile || isUploadingLogo || isDeletingLogo}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            {isUploadingLogo ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Upload en cours...
              </>
            ) : (
              <>
                <ImageIcon className="h-4 w-4 mr-2" />
                Changer le logo
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
